import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MoreVertical, ImageOff, RefreshCw, Trash2, Edit2 } from 'lucide-react';

import { theme } from '../styles/theme';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import { useToast } from '../components/ui/Toast';
import {
  deleteScanAssets,
  deleteScanRecord,
  getSignedScanAssetUrl,
  listUserScans,
  updateScanRecord,
  type ListUserScansResponse,
  type ScanRecord,
} from '../services/scanService';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2rem max(1rem, env(safe-area-inset-right, 0px)) calc(4rem + env(safe-area-inset-bottom, 0px))
    max(1rem, env(safe-area-inset-left, 0px));
  -webkit-tap-highlight-color: transparent;

  @media (max-width: 1023px) {
    padding: 1rem max(0.75rem, env(safe-area-inset-right, 0px)) calc(3rem + env(safe-area-inset-bottom, 0px))
      max(0.75rem, env(safe-area-inset-left, 0px));
  }
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 1023px) {
    gap: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  > button {
    width: 100%;
    min-height: 48px;
  }

  @media (min-width: ${theme.breakpoints.sm}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    > button {
      width: auto;
      min-height: unset;
      flex-shrink: 0;
    }
  }
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: clamp(2rem, 4vw, 2.5rem);
  color: ${theme.colors.text.primary};
  margin: 0;

  @media (max-width: 1023px) {
    font-size: 1.5rem;
  }
`;

const Description = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  margin: 0;

  @media (max-width: 1023px) {
    font-size: 0.75rem;
  }
`;

const ResultsList = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: ${theme.breakpoints.md}) {
    gap: 1.5rem;
  }
`;

const ResultCard = styled(Card)`
  display: grid;
  gap: 1rem;
  padding: 1rem 1rem 1.1rem;
  grid-template-columns: 1fr;
  align-items: stretch;

  @media (min-width: ${theme.breakpoints.md}) {
    gap: 1rem;
    padding: 1.5rem;
    grid-template-columns: 160px 1fr 200px;
    align-items: center;
  }
`;

const PreviewStack = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;

  img {
    width: 100%;
    border-radius: ${theme.borderRadius.lg};
    object-fit: cover;
    height: clamp(88px, 28vw, 120px);
    box-shadow: ${theme.shadows.sm};
  }

  @media (min-width: ${theme.breakpoints.md}) {
    gap: 0.5rem;

    img {
      height: 110px;
    }
  }
`;

const ThumbnailPlaceholder = styled.div`
  min-height: clamp(88px, 28vw, 120px);
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.secondary};
  border: 1px dashed ${theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.tertiary};
  font-size: 0.7rem;
  gap: 0.25rem;
  flex-direction: column;
  text-align: center;
  padding: 0.35rem;

  svg {
    width: 16px;
    height: 16px;
  }

  @media (min-width: ${theme.breakpoints.md}) {
    min-height: 110px;
    height: 110px;
    font-size: ${theme.typography.fontSize.sm};
    gap: 0.4rem;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;

  @media (min-width: ${theme.breakpoints.md}) {
    gap: 0.75rem;
  }
`;

const MetaTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;

  @media (min-width: ${theme.breakpoints.md}) {
    gap: 0.75rem;
  }
`;

const MetaTitle = styled.h2`
  margin: 0;
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  max-width: 100%;

  @media (min-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.lg};
    display: block;
    -webkit-line-clamp: unset;
    line-clamp: unset;
    white-space: normal;
  }
`;

const MetaDetails = styled.div`
  display: grid;
  gap: 0.25rem;
  color: ${theme.colors.text.secondary};
  font-size: 0.75rem;

  @media (min-width: ${theme.breakpoints.md}) {
    gap: 0.35rem;
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const ProgressIndicator = styled.span<{ $positive: boolean; $neutral: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${props => 
    props.$neutral 
      ? theme.colors.text.secondary 
      : props.$positive 
      ? theme.colors.mint[700] 
      : theme.colors.accent.peach};
  font-size: ${theme.typography.fontSize.sm};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  align-items: center;
  justify-content: flex-end;
  justify-self: stretch;
  width: 100%;

  > button:first-of-type {
    flex: 1 1 auto;
    min-height: 48px;
  }

  @media (min-width: ${theme.breakpoints.md}) {
    flex-wrap: wrap;
    gap: 0.75rem;
    justify-self: end;
    width: auto;

    > button:first-of-type {
      flex: 0 1 auto;
      min-height: unset;
    }
  }
`;

const OverflowContainer = styled.div`
  position: relative;
`;

const OverflowButton = styled.button`
  border: none;
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.full};
  padding: 0.5rem;
  min-width: 48px;
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: background 0.2s ease;
  touch-action: manipulation;

  &:hover {
    background: ${theme.colors.glow.mint};
    color: ${theme.colors.primary[600]};
  }

  @media (min-width: ${theme.breakpoints.md}) {
    min-width: 40px;
    min-height: 40px;
    padding: 0.4rem;
  }
`;

const OverflowMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  left: auto;
  min-width: min(220px, calc(100vw - 2rem));
  max-width: calc(100vw - 2rem);
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  border: 1px solid ${theme.colors.border.light};
  z-index: 10;
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.md}) {
    right: 0;
    left: auto;
  }
`;

const OverflowItem = styled.button`
  width: 100%;
  padding: 0.85rem 1rem;
  min-height: 48px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: background 0.15s ease;
  touch-action: manipulation;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${theme.colors.glow.mint};
    color: ${theme.colors.primary[600]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: transparent;
  }

  @media (min-width: ${theme.breakpoints.md}) {
    min-height: unset;
    padding: 0.75rem 1rem;
  }
`;

const EmptyState = styled(Card)`
  text-align: center;
  padding: 2.25rem 1.25rem;
  display: grid;
  gap: 1rem;
  align-items: center;

  @media (min-width: ${theme.breakpoints.md}) {
    padding: 3rem 2rem;
  }
`;

const ErrorState = styled(Card)`
  border: 1px solid ${theme.colors.status.error};
  color: ${theme.colors.status.error};
  padding: 1.25rem;
`;

const LoadMoreButton = styled(Button)`
  justify-self: center;
  width: 100%;
  max-width: 320px;
  min-height: 48px;

  @media (min-width: ${theme.breakpoints.md}) {
    width: auto;
    min-height: unset;
  }
`;

type BadgeVariant = 'primary' | 'success' | 'warning' | 'info' | 'popular' | 'destructive';

const statusVariant: Record<string, BadgeVariant> = {
  uploaded: 'info',
  processing: 'warning',
  complete: 'success',
  error: 'destructive',
  deleting: 'warning',
};

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    console.warn('Invalid date provided to formatDate', iso);
    return iso;
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

interface PendingActionsState {
  downloading?: 'front' | 'side' | null;
  deleting?: boolean;
  renaming?: boolean;
}

interface ScanWithProgress extends ScanRecord {
  createdAtFormatted: string;
  progressDelta?: number | null;
}

const ResultsIndexPage: React.FC = () => {
  const navigate = useNavigate();
  const { getCurrentSession } = useAuth();
  const { showToast } = useToast();
  const [scans, setScans] = useState<ScanRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastKey, setLastKey] = useState<ListUserScansResponse['lastEvaluatedKey']>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [pendingActions, setPendingActions] = useState<Record<string, PendingActionsState>>({});
  const menuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const closeMenu = () => setOpenMenuId(null);

  useEffect(() => {
    if (!openMenuId) return;

    const handleClickAway = (event: MouseEvent) => {
      const currentMenu = menuRefs.current[openMenuId];
      if (currentMenu && !currentMenu.contains(event.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [openMenuId]);

  const fetchScans = useCallback(
    async (append: boolean = false) => {
      setError(null);
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const session = await getCurrentSession();
        if (!session) {
          setScans([]);
          throw new Error('Please sign in to view your results.');
        }

        const uid = session.getIdToken().payload.sub;
        const response = await listUserScans(session, uid, 20, append ? lastKey : undefined);
        setLastKey(response.lastEvaluatedKey);
        setScans((prev) => {
          if (!append) {
            return response.items;
          }
          const existingIds = new Set(prev.map((item) => item.scanId));
          const newItems = response.items.filter((item) => !existingIds.has(item.scanId));
          return [...prev, ...newItems];
        });
      } catch (err: any) {
        // Try to get uid for logging (best effort)
        let uidForLogging = 'unknown';
        try {
          const session = await getCurrentSession();
          if (session) {
            uidForLogging = session.getIdToken().payload.sub;
          }
        } catch {
          // Ignore - we're already in error handling
        }

        console.error('[DEBUG] Failed to load scans', {
          error: err,
          name: err?.name,
          code: err?.code,
          message: err?.message,
          userId: uidForLogging,
        });
        
        // Check if it's an AWS permission error
        const isPermissionError = 
          err?.name === 'AccessDeniedException' ||
          err?.code === 'AccessDeniedException' ||
          err?.message?.includes('not authorized') ||
          err?.message?.includes('dynamodb:Query') ||
          err?.message?.includes('no identity-based policy');
        
        // Check if it's an empty result (no scans)
        const isEmpty = err?.message?.includes('No items found') || 
                      err?.code === 'ResourceNotFoundException';
        
        if (isPermissionError) {
          // Log for debugging but show empty state instead of error
          console.warn('[DEBUG] (Dev only) DynamoDB access error – check IAM policy for CognitoGlowLensAuthRole', {
            userId: uidForLogging,
            error: err,
          });
          // Treat permission errors as empty state - user likely has no scans yet
          // or permissions need to be configured
          setScans([]);
          setError(null); // Don't show error, show empty state instead
        } else if (isEmpty) {
          // Explicitly empty result
          console.log('[DEBUG] Query returned no items (user has no scans)', { userId: uidForLogging });
          setScans([]);
          setError(null);
        } else {
          // Other errors (network, etc.) - show error message
          console.error('[DEBUG] Query error (non-permission)', {
            userId: uidForLogging,
            error: err,
          });
          setError(
            err instanceof Error ? err.message : 'We could not load your scans. Please try again.'
          );
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [getCurrentSession, lastKey]
  );

  useEffect(() => {
    void fetchScans(false);
  }, [fetchScans]);

  const handleDownload = useCallback(
    async (scan: ScanRecord, slot: 'front' | 'side') => {
      setPendingActions((prev) => ({
        ...prev,
        [scan.scanId]: {
          ...prev[scan.scanId],
          downloading: slot,
        },
      }));

      try {
        const session = await getCurrentSession();
        if (!session) {
          throw new Error('Session expired. Please sign in again.');
        }

        const key =
          slot === 'front' ? scan.storageKeys.front : scan.storageKeys.side;
        const signedUrl = await getSignedScanAssetUrl(session, key, 60);
        const link = document.createElement('a');
        link.href = signedUrl;
        link.download = `glowlens-${slot}-${scan.scanId}.jpg`;
        link.click();
      } catch (err) {
        console.error('Download error', err);
        showToast(
          err instanceof Error ? err.message : 'We could not download that photo right now.',
          'error'
        );
      } finally {
        setPendingActions((prev) => ({
          ...prev,
          [scan.scanId]: {
            ...prev[scan.scanId],
            downloading: null,
          },
        }));
        closeMenu();
      }
    },
    [getCurrentSession]
  );

  const handleRename = useCallback(
    async (scan: ScanRecord) => {
      const currentName = scan.customName || scan.scanId;
      const newName = window.prompt('Enter a new name for this scan:', currentName);
      
      if (!newName || newName.trim() === '' || newName === currentName) {
        return; // User cancelled or didn't change the name
      }

      const trimmedName = newName.trim();
      if (trimmedName.length > 50) {
        showToast('Scan name must be 50 characters or less.', 'error');
        return;
      }

      setPendingActions((prev) => ({
        ...prev,
        [scan.scanId]: {
          ...prev[scan.scanId],
          renaming: true,
        },
      }));

      try {
        const session = await getCurrentSession();
        if (!session) {
          throw new Error('Session expired. Please sign in again.');
        }

        const uid = session.getIdToken().payload.sub;
        await updateScanRecord(session, uid, scan.timestamp, {
          customName: trimmedName,
        });

        // Update local state
        setScans((prev) =>
          prev.map((item) =>
            item.scanId === scan.scanId ? { ...item, customName: trimmedName } : item
          )
        );

        showToast('Scan renamed successfully.', 'success');
      } catch (err) {
        console.error('Rename error', err);
        showToast(
          err instanceof Error ? err.message : 'We could not rename that scan. Please try again later.',
          'error'
        );
      } finally {
        setPendingActions((prev) => ({
          ...prev,
          [scan.scanId]: {
            ...prev[scan.scanId],
            renaming: false,
          },
        }));
        closeMenu();
      }
    },
    [getCurrentSession, showToast]
  );

  const handleDelete = useCallback(
    async (scan: ScanRecord) => {
      const confirmDelete = window.confirm(
        'Delete this scan? This will remove the photos and analysis for this scan.'
      );
      if (!confirmDelete) return;

      setPendingActions((prev) => ({
        ...prev,
        [scan.scanId]: {
          ...prev[scan.scanId],
          deleting: true,
        },
      }));

      try {
        const session = await getCurrentSession();
        if (!session) {
          throw new Error('Session expired. Please sign in again.');
        }

        await deleteScanAssets(session, [
          {
            key: scan.storageKeys.front,
            thumbKey: scan.storageKeys.frontThumb,
            url: scan.front.url,
            thumbUrl: scan.front.thumbUrl,
            metadata: scan.front.metadata,
          },
          {
            key: scan.storageKeys.side,
            thumbKey: scan.storageKeys.sideThumb,
            url: scan.side.url,
            thumbUrl: scan.side.thumbUrl,
            metadata: scan.side.metadata,
          },
        ]);

        await deleteScanRecord(session, scan.uid, scan.timestamp);
        setScans((prev) => prev.filter((item) => item.scanId !== scan.scanId));
        window.dispatchEvent(new CustomEvent('glowlens:scanDeleted', { detail: { scanId: scan.scanId } }));
        showToast('Scan deleted.', 'success');
      } catch (err) {
        console.error('Delete error', err);
        showToast(
          err instanceof Error ? err.message : 'We could not delete that scan. Please try again later.',
          'error'
        );
      } finally {
        setPendingActions((prev) => ({
          ...prev,
          [scan.scanId]: {
            ...prev[scan.scanId],
            deleting: false,
          },
        }));
        closeMenu();
      }
    },
    [getCurrentSession, showToast]
  );

  const groupedByDay = useMemo(() => {
    // Sort scans by date (newest first) to calculate progress
    const sortedScans = [...scans].sort((a, b) => {
      const dateA = new Date(a.createdAt ?? a.timestamp).getTime();
      const dateB = new Date(b.createdAt ?? b.timestamp).getTime();
      return dateB - dateA;
    });

    return sortedScans.map((scan, index) => {
      // Find the previous scan (next in array since sorted newest first)
      const previousScan = index < sortedScans.length - 1 ? sortedScans[index + 1] : null;
      
      // Calculate progress delta
      let progressDelta: number | null = null;
      if (scan.status === 'complete') {
        const currentIndex = scan.analysis?.skinHealthIndex ?? scan.glowScore;
        if (previousScan && typeof currentIndex === 'number') {
          const previousIndex = previousScan.analysis?.skinHealthIndex ?? previousScan.glowScore;
          if (typeof previousIndex === 'number') {
            progressDelta = currentIndex - previousIndex;
          }
        }
      }

      return {
        ...scan,
        createdAtFormatted: formatDate(scan.createdAt ?? scan.timestamp),
        progressDelta,
      } as ScanWithProgress;
    });
  }, [scans]);

  return (
    <PageContainer>
      <Container>
        <Header>
          <div>
            <Title>Results</Title>
            <Description>Review your glow scans and track improvements over time.</Description>
          </div>
          <Button variant="secondary" onClick={() => navigate('/scan')}>
            Upload New Scan
          </Button>
        </Header>

        {error && (
          <ErrorState>
            <strong>We hit a snag.</strong>
            <p>{error}</p>
            <Button variant="outline" size="sm" onClick={() => fetchScans(false)}>
              <RefreshCw size={16} />
              Try again
            </Button>
          </ErrorState>
        )}

        {!loading && !scans.length && !error && (
          <EmptyState>
            <div style={{ fontSize: '3rem' }}>📷</div>
            <h2>No scans yet</h2>
            <p>Upload front & side photos to see your first GlowLens result.</p>
            <Button onClick={() => navigate('/scan')}>Upload New Scan</Button>
          </EmptyState>
        )}

        <ResultsList>
          {groupedByDay.map((scan) => {
            const isMenuOpen = openMenuId === scan.scanId;
            const actionState = pendingActions[scan.scanId] || {};
            return (
              <ResultCard key={scan.scanId}>
                <PreviewStack>
                  {scan.front?.thumbUrl ? (
                    <img src={scan.front.thumbUrl} alt="Front view thumbnail" />
                  ) : (
                    <ThumbnailPlaceholder>
                      <ImageOff />
                      No preview
                    </ThumbnailPlaceholder>
                  )}
                  {scan.side?.thumbUrl ? (
                    <img src={scan.side.thumbUrl} alt="Side view thumbnail" />
                  ) : (
                    <ThumbnailPlaceholder>
                      <ImageOff />
                      No preview
                    </ThumbnailPlaceholder>
                  )}
                </PreviewStack>

                <Meta>
                  <MetaTop>
                    <MetaTitle>Scan • {scan.customName || scan.scanId}</MetaTitle>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      {scan.status === 'processing' ? (
                        <Badge variant="warning">Analyzing…</Badge>
                      ) : scan.analysis ? (
                        <>
                          <Badge variant={statusVariant[scan.status] ?? 'primary'}>
                            {scan.analysis.skinHealthStatus}
                          </Badge>
                          <Badge variant="info" size="sm">
                            {scan.analysis.acneSeverity}
                          </Badge>
                        </>
                      ) : (
                        <Badge variant={statusVariant[scan.status] ?? 'primary'}>
                          {scan.status}
                        </Badge>
                      )}
                    </div>
                  </MetaTop>
                  <MetaDetails>
                    <span>{scan.createdAtFormatted}</span>
                    {scan.status === 'complete' && scan.analysis ? (
                      <>
                        <span>
                          Skin Health Index™: <strong>{scan.analysis.skinHealthIndex}</strong>
                          {typeof scan.progressDelta === 'number' && scan.progressDelta !== 0 && (
                            <ProgressIndicator 
                              $positive={scan.progressDelta > 0} 
                              $neutral={false}
                            >
                              {scan.progressDelta > 0 ? '↑' : '↓'} 
                              {scan.progressDelta > 0 ? '+' : ''}
                              {scan.progressDelta.toFixed(1)}
                            </ProgressIndicator>
                          )}
                          {typeof scan.progressDelta === 'number' && scan.progressDelta === 0 && (
                            <ProgressIndicator $positive={false} $neutral={true}>
                              Stable
                            </ProgressIndicator>
                          )}
                        </span>
                        <span>
                          Severity: <strong>{scan.analysis.acneSeverity}</strong> • Focus: <strong>{scan.analysis.acneFocusArea}</strong>
                        </span>
                      </>
                    ) : typeof scan.glowScore === 'number' ? (
                      <span>Skin Health Index™: <strong>{scan.glowScore}</strong></span>
                    ) : null}
                    {scan.status === 'processing' && (
                      <span style={{ fontStyle: 'italic', color: theme.colors.text.tertiary }}>
                        Analysis in progress...
                      </span>
                    )}
                  </MetaDetails>
                </Meta>

                <Actions>
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/results/${scan.scanId}`, {
                      state: {
                        scanTimestamp: scan.timestamp,
                      },
                    })}
                  >
                    View details
                  </Button>

                  <OverflowContainer ref={(node) => { menuRefs.current[scan.scanId] = node; }}>
                    <OverflowButton
                      aria-label="More actions"
                      onClick={() => setOpenMenuId((prev) => (prev === scan.scanId ? null : scan.scanId))}
                    >
                      <MoreVertical />
                    </OverflowButton>
                    {isMenuOpen && (
                      <OverflowMenu>
                        <OverflowItem
                          onClick={() => handleRename(scan)}
                          disabled={actionState.renaming}
                        >
                          <Edit2 />
                          {actionState.renaming ? 'Renaming…' : 'Rename Scan'}
                        </OverflowItem>
                        <OverflowItem
                          onClick={() => handleDelete(scan)}
                          disabled={actionState.deleting}
                        >
                          <Trash2 />
                          {actionState.deleting ? 'Deleting…' : 'Delete Scan'}
                        </OverflowItem>
                      </OverflowMenu>
                    )}
                  </OverflowContainer>
                </Actions>
              </ResultCard>
            );
          })}
        </ResultsList>

        {loading && (
          <Card padding="lg">
            <p style={{ margin: 0 }}>Loading your scans…</p>
          </Card>
        )}

        {lastKey && scans.length > 0 && (
          <LoadMoreButton
            variant="outline"
            onClick={() => fetchScans(true)}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </LoadMoreButton>
        )}
      </Container>
    </PageContainer>
  );
};

export default ResultsIndexPage;

